import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import TrialCountdown from '../components/TrialCountdown';
import { 
  Send, 
  Mic, 
  MicOff, 
  ArrowLeft, 
  AlertTriangle,
  Volume2,
  VolumeX,
  Square,
  Clock,
  CheckCircle,
  Heart,
  Brain,
  Sparkles,
  MessageCircle,
  User,
  Bot,
  Shield,
  Zap,
  Star,
  Activity
} from 'lucide-react';
import api, { API_BASE_URL } from '../utils/api';
import { getSpecialtyColor } from '../utils/forceRefresh';
import ttsService, { getTherapistVoice } from '../utils/textToSpeech';

const Chat = () => {
  const { therapistId } = useParams();
  const { user } = useAuth();
  const { hasAccessToTherapist } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get sessionId from URL parameters if it exists
  const urlParams = new URLSearchParams(location.search);
  const existingSessionId = urlParams.get('sessionId');
  
  const [therapist, setTherapist] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showCrisisAlert, setShowCrisisAlert] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [assessmentStatus, setAssessmentStatus] = useState(null);
  const [showAssessmentPrompt, setShowAssessmentPrompt] = useState(false);
  const [speakingMessageId, setSpeakingMessageId] = useState(null);
  const [autoPlayVoice, setAutoPlayVoice] = useState(false);
  
  const messagesEndRef = useRef(null);
  const audioRef = useRef(null);
  const recognitionRef = useRef(null);

  // Crisis keywords for detection
  const crisisKeywords = [
    'suicide', 'kill myself', 'want to die', 'end it all', 'self-harm',
    'hurt myself', 'no reason to live', 'better off dead', 'give up'
  ];

  useEffect(() => {
    fetchTherapist();
    fetchChatHistory();
  }, [therapistId, existingSessionId]);

  // Check assessment status when component mounts or when returning from assessment
  useEffect(() => {
    if (assessmentStatus) {
      setShowAssessmentPrompt(!assessmentStatus.hasCompleted);
    }
  }, [assessmentStatus]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Cleanup speech recognition and TTS on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      ttsService.stop();
    };
  }, []);

  // Auto-play new AI messages if enabled
  useEffect(() => {
    if (autoPlayVoice && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.sender === 'therapist' && lastMessage._id) {
        // Small delay to ensure message is rendered
        setTimeout(() => {
          speakMessage(lastMessage._id, lastMessage.content);
        }, 500);
      }
    }
  }, [messages, autoPlayVoice]);

  const fetchTherapist = async () => {
    try {
      // Get all therapists and find the one we need
      const response = await api.get('/therapists');
      const therapists = response.data.therapists || response.data;
      const therapist = therapists.find(t => t.id === therapistId);
      if (therapist) {
        setTherapist(therapist);
      } else {
        console.error('Therapist not found');
        navigate('/app');
      }
    } catch (error) {
      console.error('Failed to fetch therapist:', error);
      navigate('/app');
    }
  };

  const fetchChatHistory = async () => {
    try {
      let sessionResponse;
      
      if (existingSessionId) {
        // Load existing session
        console.log('Loading existing session:', existingSessionId);
        const sessionData = await api.get(`/chat/session/${existingSessionId}`);
        
        const chat = sessionData.data.chat;
        setSessionId(chat.sessionId);
        
        // Convert messages to the format expected by the UI
        const formattedMessages = chat.messages.map((msg, index) => ({
          _id: msg._id || `msg_${index}`,
          content: msg.content,
          sender: msg.role === 'user' ? 'user' : 'therapist',
          timestamp: new Date(msg.timestamp),
          mood: msg.metadata?.mood
        }));
        
        setMessages(formattedMessages);
        
        // Get assessment status for this therapist
        const assessmentResponse = await api.post(`/chat/start/${therapistId}`, {
          initialMessage: null
        });
        setAssessmentStatus(assessmentResponse.data.assessmentStatus);
        
        return;
      }
      
      // Create a new session
              sessionResponse = await api.post(`/chat/start/${therapistId}`, {
        initialMessage: null
      });
      
      setSessionId(sessionResponse.data.sessionId);
      setAssessmentStatus(sessionResponse.data.assessmentStatus);
      
      // Debug logging
      console.log('Session response:', sessionResponse.data);
      console.log('Assessment status:', sessionResponse.data.assessmentStatus);
      console.log('Assessment status exists:', !!sessionResponse.data.assessmentStatus);
      console.log('Assessment completed:', sessionResponse.data.assessmentStatus?.hasCompleted);
      
      // Check if user needs to be prompted for assessment
      if (sessionResponse.data.assessmentStatus && !sessionResponse.data.assessmentStatus.hasCompleted) {
        console.log('🔄 User needs assessment - showing assessment prompt');
        setShowAssessmentPrompt(true);
        // Start with empty messages - user will type their first message
        setMessages([]);
      } else {
        console.log('✅ Assessment completed or not required, starting with empty messages');
        // Start with empty messages - welcome will be shown in UI
        setMessages([]);
      }
    } catch (error) {
      console.error('Failed to create session:', error);
      // Fallback to empty messages
      setMessages([]);
    }
  };

  const handleAssessmentResponse = async (hasTime) => {
    if (hasTime) {
      // Navigate to assessment
      navigate(`/app/assessment/${assessmentStatus.assessmentId}?returnTo=app/chat/${therapistId}`);
    } else {
      // Dismiss assessment prompt and send a message to the AI therapist
      setShowAssessmentPrompt(false);
      
      // Send a message to the AI therapist indicating the user doesn't want to do assessment now
      const userMessage = {
        _id: Date.now(),
        content: 'I\'d prefer to talk about my situation directly without doing an assessment right now.',
        sender: 'user',
        timestamp: new Date()
      };

      setMessages([userMessage]);
      setLoading(true);

      try {
        const response = await api.post(`/chat/message/${sessionId || 'new-session'}`, {
          message: userMessage.content,
          therapistId: therapistId
        });

        const responseContent = response.data.response?.content || response.data.response;
        const responseTimestamp = response.data.response?.timestamp || response.data.timestamp;
        
        const parsedTimestamp = responseTimestamp ? new Date(responseTimestamp) : new Date();
        
        const aiMessage = {
          _id: Date.now() + 1,
          content: responseContent,
          sender: 'therapist',
          timestamp: parsedTimestamp,
          therapistId: therapistId
        };

        setMessages(prev => [...prev, aiMessage]);
      } catch (error) {
        console.error('Failed to send message:', error);
        setMessages(prev => [...prev, {
          _id: Date.now(),
          content: 'Sorry, I\'m having trouble responding right now. Please try again.',
          sender: 'system',
          timestamp: new Date()
        }]);
      } finally {
        setLoading(false);
      }
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const detectCrisis = (text) => {
    const lowerText = text.toLowerCase();
    return crisisKeywords.some(keyword => lowerText.includes(keyword));
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || loading) return;

    const messageText = newMessage.trim();
    const isCrisis = detectCrisis(messageText);
    
    if (isCrisis) {
      setShowCrisisAlert(true);
      // Remove timeout - crisis alert should stay visible
    }

    // Don't hide assessment prompt - let it stay visible until user completes assessment
    // The prompt will only be hidden when user clicks "Yes, I have time" or "Maybe later"

    const userMessage = {
      _id: Date.now(),
      content: messageText,
      sender: 'user',
      timestamp: new Date(),
      isCrisis
    };

    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    setLoading(true);

    try {
      // Ensure we have a session ID
      let currentSessionId = sessionId;
      if (!currentSessionId) {
        console.log('No session ID found, creating new session...');
        const sessionResponse = await api.post(`/chat/start/${therapistId}`, {
          initialMessage: null
        });
        currentSessionId = sessionResponse.data.sessionId;
        setSessionId(currentSessionId);
        console.log('Created new session:', currentSessionId);
      }

      const response = await api.post(`/chat/message/${currentSessionId}`, {
        message: messageText,
        therapistId: therapistId
      });

      // Handle both response formats (main server vs test server)
      // Main server: response.data.response.content and response.data.response.timestamp
      // Test server: response.data.response and response.data.timestamp
      const responseContent = response.data.response?.content || response.data.response;
      const responseTimestamp = response.data.response?.timestamp || response.data.timestamp;
      
      const parsedTimestamp = responseTimestamp ? new Date(responseTimestamp) : new Date();
      
      const aiMessage = {
        _id: Date.now() + 1,
        content: responseContent,
        sender: 'therapist',
        timestamp: parsedTimestamp,
        therapistId: therapistId
      };

      setMessages(prev => [...prev, aiMessage]);
    } catch (error) {
      console.error('Failed to send message:', error);
      // Add error message
      setMessages(prev => [...prev, {
        _id: Date.now(),
        content: 'Sorry, I\'m having trouble responding right now. Please try again.',
        sender: 'system',
        timestamp: new Date()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const startVoiceRecording = () => {
    // Check if browser supports speech recognition
    if (!window.SpeechRecognition && !window.webkitSpeechRecognition) {
      alert('Voice recording is not supported in your browser. Please use Chrome, Edge, or Safari.');
      return;
    }

    // Check if microphone permission is available
    if (navigator.permissions) {
      navigator.permissions.query({ name: 'microphone' }).then((result) => {
        if (result.state === 'denied') {
          alert('Microphone access is denied. Please enable microphone access in your browser settings.');
          return;
        }
      });
    }

    // Initialize speech recognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    recognitionRef.current = new SpeechRecognition();
    
    // Configure recognition settings
    recognitionRef.current.continuous = false;
    recognitionRef.current.interimResults = true;
    recognitionRef.current.lang = 'en-US';
    
    let finalTranscript = '';
    
    recognitionRef.current.onstart = () => {
      setIsRecording(true);
      setNewMessage('Listening...');
      console.log('Voice recording started');
    };
    
    recognitionRef.current.onresult = (event) => {
      let interimTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }
      
      setNewMessage(finalTranscript + interimTranscript);
    };
    
    recognitionRef.current.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setIsRecording(false);
      
      let errorMessage = 'Voice recording error. Please try again.';
      
      if (event.error === 'not-allowed') {
        errorMessage = 'Please allow microphone access to use voice recording.';
      } else if (event.error === 'no-speech') {
        errorMessage = 'No speech detected. Please try again.';
      } else if (event.error === 'audio-capture') {
        errorMessage = 'No microphone found. Please check your microphone connection.';
      } else if (event.error === 'network') {
        errorMessage = 'Network error. Please check your internet connection.';
      }
      
      alert(errorMessage);
      setNewMessage('');
    };
    
    recognitionRef.current.onend = () => {
      setIsRecording(false);
      console.log('Voice recording ended');
      
      // Keep the final transcript in the input field
      if (finalTranscript.trim()) {
        setNewMessage(finalTranscript.trim());
        console.log('Final transcript:', finalTranscript.trim());
      }
    };
    
    // Start recording
    try {
      recognitionRef.current.start();
    } catch (error) {
      console.error('Failed to start speech recognition:', error);
      alert('Failed to start voice recording. Please try again.');
      setIsRecording(false);
    }
  };

  const stopVoiceRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
  };

  // Text-to-Speech functions
  const speakMessage = (messageId, text) => {
    // Stop any current speech
    if (speakingMessageId) {
      ttsService.stop();
      setSpeakingMessageId(null);
    }

    // Get therapist voice configuration (pass therapist data for backend voice settings)
    const voiceConfig = getTherapistVoice(therapistId, therapist);

    // Speak the message
    ttsService.speak(text, voiceConfig, {
      onStart: () => {
        setSpeakingMessageId(messageId);
      },
      onEnd: () => {
        setSpeakingMessageId(null);
      },
      onError: (error) => {
        console.error('TTS error:', error);
        setSpeakingMessageId(null);
        if (error === 'not-allowed') {
          alert('Speech synthesis is not allowed. Please check your browser settings.');
        } else if (error !== 'interrupted') {
          alert('Unable to play voice. Please try again.');
        }
      }
    });
  };

  const stopSpeaking = () => {
    ttsService.stop();
    setSpeakingMessageId(null);
  };

  const playAudio = (audioUrl) => {
    if (audioRef.current) {
      audioRef.current.src = audioUrl;
      audioRef.current.play();
      setIsPlaying(true);
      
      audioRef.current.onended = () => {
        setIsPlaying(false);
      };
    }
  };

  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  const handleEndSession = async () => {
    if (!window.confirm('Are you sure you want to end this session? This will mark the session as completed.')) {
      return;
    }
    
    try {
      if (sessionId) {
        // Call the end session API with the actual session ID
        await api.put(`/chat/end/${sessionId}`);
      }
      
      // Navigate back to home
      navigate('/app');
    } catch (error) {
      console.error('Failed to end session:', error);
      alert('Failed to end session. Please try again.');
    }
  };

  if (!therapist) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-100 to-purple-100 rounded-3xl mb-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading Therapist...</h3>
          <p className="text-gray-600">Preparing your therapy session</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Trial Countdown */}
      <TrialCountdown />
      
      {/* Beautiful Header */}
      <div className="bg-gradient-to-br from-white to-blue-50 border-b border-blue-100 px-6 py-4 shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/app')}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200 transform hover:scale-105"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-2xl overflow-hidden shadow-lg">
                {therapist.avatar ? (
                  <img 
                    src={therapist.avatar} 
                    alt={`${therapist.name} avatar`}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-500 rounded-2xl flex items-center justify-center" 
                     style={{ display: therapist.avatar ? 'none' : 'flex' }}>
                  <span className="text-xl font-bold text-white">
                    {therapist.name.charAt(0)}
                  </span>
                </div>
              </div>
              
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
                  <span>{therapist.name}</span>
                  <div className="p-1 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full">
                    <div className="w-2 h-2 bg-white rounded-full"></div>
                  </div>
                </h1>
                <p className="text-sm text-gray-600 flex items-center space-x-2">
                  <Brain className="w-3 h-3" />
                  <span>{therapist.specialty} Specialist</span>
                </p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <span className={`px-3 py-1 rounded-full text-xs font-semibold text-white shadow-sm ${getSpecialtyColor(therapist.specialty)}`}>
              {therapist.specialty}
            </span>
            
            <button
              onClick={() => setAutoPlayVoice(!autoPlayVoice)}
              className={`p-2 rounded-xl transition-all duration-200 transform hover:scale-105 ${
                autoPlayVoice 
                  ? 'text-blue-600 bg-blue-50' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
              title={autoPlayVoice ? 'Auto-play voice enabled' : 'Auto-play voice disabled'}
            >
              {autoPlayVoice ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
            
            <button
              onClick={handleEndSession}
              className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200 transform hover:scale-105"
              title="End session"
            >
              <Square className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Crisis Alert */}
      {showCrisisAlert && (
        <div className="bg-gradient-to-r from-red-50 to-pink-50 border-l-4 border-red-500 p-4 shadow-lg">
          <div className="flex items-center">
            <div className="p-2 bg-gradient-to-br from-red-400 to-pink-500 rounded-lg mr-3">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-red-700 font-semibold text-lg">Crisis Detected</p>
              <p className="text-red-600 text-sm mt-1">
                If you're in crisis, please call <strong>988</strong> (US) or your local crisis hotline immediately. 
                This is not a substitute for emergency care.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Assessment Prompt */}
      {console.log('🔍 Assessment prompt state:', showAssessmentPrompt)}
      {showAssessmentPrompt && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-6 shadow-lg">
          {console.log('🎯 Assessment prompt component rendered')}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-4 lg:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex-shrink-0">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-blue-800 font-bold text-lg">Complete Assessment for Personalized Support</p>
                <p className="text-blue-700 text-sm mt-1">
                  Take a 5-10 minute assessment to help me provide more tailored and effective support for your specific situation.
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3">
              <button
                onClick={() => handleAssessmentResponse(true)}
                className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl hover:from-blue-600 hover:to-indigo-600 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 flex items-center justify-center space-x-2"
              >
                <Sparkles className="w-4 h-4" />
                <span>Take Assessment</span>
              </button>
              <button
                onClick={() => handleAssessmentResponse(false)}
                className="w-full sm:w-auto px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-all duration-200 font-medium"
              >
                Continue Chat
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Welcome message when no messages exist */}
        {messages.length === 0 && therapist && (
          <div className="flex justify-start">
            <div className="bg-gradient-to-br from-white to-blue-50 border-2 border-blue-100 rounded-2xl px-6 py-4 max-w-md shadow-lg">
              <div className="flex items-center space-x-3 mb-3">
                <div className="p-2 bg-gradient-to-br from-blue-400 to-purple-500 rounded-lg">
                  <Heart className="w-4 h-4 text-white" />
                </div>
                <h3 className="font-bold text-gray-900">Welcome!</h3>
              </div>
              <p className="text-gray-700 leading-relaxed">
                Hello! I'm <strong>{therapist.name}</strong>, your {therapist.specialty} specialist. I'm here to support you on your journey to better mental health. How can I help you today?
              </p>
              <div className="flex items-center space-x-2 mt-3 text-xs text-gray-500">
                <Clock className="w-3 h-3" />
                <span>{new Date().toLocaleTimeString()}</span>
              </div>
            </div>
          </div>
        )}
        
        {messages.map((message) => (
          <div
            key={message._id}
            className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-md px-6 py-4 rounded-2xl shadow-lg ${
              message.sender === 'user'
                ? 'bg-gradient-to-br from-blue-500 to-purple-600 text-white'
                : message.sender === 'system'
                ? 'bg-gradient-to-br from-gray-100 to-gray-200 text-gray-800 border-2 border-gray-200'
                : 'bg-gradient-to-br from-white to-blue-50 text-gray-900 border-2 border-blue-100'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  {message.sender === 'user' ? (
                    <User className="w-4 h-4 text-blue-100" />
                  ) : message.sender === 'system' ? (
                    <AlertTriangle className="w-4 h-4 text-gray-600" />
                  ) : (
                    <Bot className="w-4 h-4 text-blue-500" />
                  )}
                  <span className="text-xs font-medium opacity-80">
                    {message.sender === 'user' ? 'You' : message.sender === 'system' ? 'System' : therapist.name}
                  </span>
                </div>
                
                {/* Voice controls for therapist messages */}
                {message.sender === 'therapist' && ttsService.isSupported() && (
                  <button
                    onClick={() => {
                      if (speakingMessageId === message._id) {
                        stopSpeaking();
                      } else {
                        speakMessage(message._id, message.content);
                      }
                    }}
                    className={`p-1.5 rounded-lg transition-all duration-200 hover:scale-110 ${
                      speakingMessageId === message._id
                        ? 'bg-blue-500 text-white'
                        : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                    }`}
                    title={speakingMessageId === message._id ? 'Stop speaking' : 'Play voice'}
                  >
                    {speakingMessageId === message._id ? (
                      <Square className="w-3.5 h-3.5" />
                    ) : (
                      <Volume2 className="w-3.5 h-3.5" />
                    )}
                  </button>
                )}
              </div>
              <p className="text-sm leading-relaxed">{message.content}</p>
              <div className="flex items-center space-x-2 mt-3 text-xs opacity-70">
                <Clock className="w-3 h-3" />
                <span>
                  {(() => {
                    try {
                      const date = new Date(message.timestamp);
                      return isNaN(date.getTime()) ? 'Invalid Date' : date.toLocaleTimeString();
                    } catch (error) {
                      return 'Invalid Date';
                    }
                  })()}
                </span>
              </div>
            </div>
          </div>
        ))}
        
        {loading && (
          <div className="flex justify-start">
            <div className="bg-gradient-to-br from-white to-blue-50 border-2 border-blue-100 rounded-2xl px-6 py-4 shadow-lg">
              <div className="flex items-center space-x-3">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                </div>
                <span className="text-sm text-gray-600 font-medium">{therapist.name} is typing...</span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Crisis Alert - Bottom */}
      {showCrisisAlert && (
        <div className="bg-gradient-to-r from-red-50 to-pink-50 border-l-4 border-red-500 p-4 shadow-lg">
          <div className="flex items-center">
            <div className="p-2 bg-gradient-to-br from-red-400 to-pink-500 rounded-lg mr-3">
              <AlertTriangle className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-red-700 font-semibold text-lg">Crisis Detected</p>
              <p className="text-red-600 text-sm mt-1">
                If you're in crisis, please call <strong>988</strong> (US) or your local crisis hotline immediately. 
                This is not a substitute for emergency care.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Beautiful Input Area */}
      <div className="bg-gradient-to-br from-white to-gray-50 border-t border-gray-200 p-6 shadow-lg">
        {/* Recording Status Indicator */}
        {isRecording && (
          <div className="mb-4 p-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-gradient-to-br from-red-400 to-pink-500 rounded-lg">
                <Mic className="w-4 h-4 text-white" />
              </div>
              <div>
                <span className="text-red-700 font-semibold">Recording...</span>
                <p className="text-red-600 text-sm">Speak now to transcribe your message</p>
              </div>
              <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
            </div>
          </div>
        )}
        
        <div className="flex items-end space-x-3">
          <div className="flex-1 relative">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isRecording ? "Listening... Speak now..." : "Type your message..."}
              className={`w-full px-4 py-3 border-2 rounded-2xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all duration-200 ${
                isRecording 
                  ? 'border-red-300 bg-red-50' 
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
              rows="1"
              disabled={loading}
            />
            {isRecording && (
              <div className="absolute top-3 right-3">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
              </div>
            )}
          </div>
          
          <button
            onClick={isRecording ? stopVoiceRecording : startVoiceRecording}
            className={`p-3 rounded-xl transition-all duration-200 transform hover:scale-105 ${
              isRecording
                ? 'bg-gradient-to-br from-red-400 to-pink-500 text-white shadow-lg'
                : 'bg-gradient-to-br from-gray-100 to-gray-200 text-gray-600 hover:from-gray-200 hover:to-gray-300'
            }`}
            disabled={loading}
            title={isRecording ? 'Stop recording' : 'Start voice recording'}
          >
            {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
          </button>
          
          <button
            onClick={handleSendMessage}
            disabled={!newMessage.trim() || loading}
            className="p-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Hidden audio element for voice messages */}
      <audio ref={audioRef} style={{ display: 'none' }} />
    </div>
  );
};

export default Chat; 